// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module AnalyticsDashboardPage
 * @package @geenius/analytics/solidjs
 * @description Composes the primary SolidJS analytics dashboard page from the
 * shared metric, table, and device widgets. The page exposes a ready-to-render
 * overview surface for traffic and engagement reporting.
 */

import {
  type AnalyticsDashboardData,
  type AnalyticsEvent,
  buildAnalyticsSeoMeta,
  type CohortAnalysis,
  type ConsentSettings,
  type FunnelResult,
  type RealtimeStats,
  type TimelineEvent,
} from "@geenius/analytics";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@geenius/ui/solidjs";
import type { Component } from "solid-js";
import { For, Show } from "solid-js";
import { DeviceBreakdown } from "../components/DeviceBreakdown";
import type { AnalyticsErrorStateValue } from "../components/ErrorState";
import { EventTable } from "../components/EventTable";
import { FunnelChart } from "../components/FunnelChart";
import { RealtimeBadge } from "../components/RealtimeBadge";
import { StatsCard } from "../components/StatsCard";
import { Timeline } from "../components/Timeline";
import { TopPagesTable } from "../components/TopPagesTable";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { cn } from "../lib/cn";
import { FadeIn } from "../lib/FadeIn";
import { PageErrorBoundary } from "./PageErrorBoundary";

type AnalyticsCopy = ReturnType<typeof createAnalyticsCopy>;

const AnalyticsSeoHead: Component<{ meta: unknown }> = (props) => {
  void props.meta;
  return null;
};

function emptyFunnelResult(copy: AnalyticsCopy): FunnelResult {
  return {
    name: copy.t("analytics.dashboard.conversions"),
    steps: [],
    totalEntries: 0,
    totalConversions: 0,
    overallConversionRate: 0,
    analyzedAt: Date.now(),
  };
}

function renderConsentStatus(
  settings: ConsentSettings | null | undefined,
  copy: AnalyticsCopy,
) {
  return (
    <Card data-analytics-consent-section>
      <CardHeader class="pb-0">
        <CardTitle class="text-lg" data-analytics-section-title>
          {copy.t("analytics.consent.state")}
        </CardTitle>
      </CardHeader>
      <CardContent class="grid gap-3 pt-4 sm:grid-cols-4">
        <For
          each={
            [
              [copy.t("analytics.consent.essential"), settings?.essential],
              [copy.t("analytics.consent.analyticsScope"), settings?.analytics],
              [copy.t("analytics.consent.marketing"), settings?.marketing],
              [
                copy.t("analytics.consent.personalization"),
                settings?.personalization,
              ],
            ] as const
          }
        >
          {([label, enabled]) => (
            <div class="rounded-xl border border-border bg-muted/30 p-3">
              <span class="text-xs font-semibold uppercase text-muted-foreground">
                {label}
              </span>
              <p class="mt-1 text-sm font-semibold text-foreground">
                {enabled === undefined
                  ? copy.t("analytics.consent.status.notRecorded")
                  : enabled
                    ? copy.t("analytics.consent.status.on")
                    : copy.t("analytics.consent.status.off")}
              </p>
            </div>
          )}
        </For>
      </CardContent>
    </Card>
  );
}

function renderCohorts(
  analysis: CohortAnalysis | null | undefined,
  copy: AnalyticsCopy,
) {
  return (
    <Card data-analytics-cohort-section>
      <CardHeader class="pb-0">
        <CardTitle class="text-lg" data-analytics-section-title>
          {copy.t("analytics.dashboard.cohorts")}
        </CardTitle>
      </CardHeader>
      <CardContent class="grid gap-3 pt-4">
        <Show
          when={analysis && analysis.cohorts.length > 0}
          fallback={
            <p class="text-sm text-muted-foreground">
              {copy.t("analytics.dashboard.noCohorts")}
            </p>
          }
        >
          <For each={analysis?.cohorts ?? []}>
            {(cohort) => (
              <div
                class="grid gap-2 rounded-xl border border-border bg-muted/30 p-3"
                data-analytics-cohort-row
              >
                <div class="flex items-center justify-between gap-3">
                  <strong class="text-sm font-semibold text-foreground">
                    {cohort.cohort}
                  </strong>
                  <Badge variant="secondary">
                    {copy.plural(
                      "analytics.dashboardBuilder.users",
                      cohort.size,
                    )}
                  </Badge>
                </div>
                <div class="grid gap-2">
                  <For each={cohort.retained}>
                    {(point) => (
                      <div class="flex justify-between gap-3 text-xs text-muted-foreground">
                        <span>
                          {copy.t("analytics.dashboardBuilder.dayLabel", {
                            day: point.day,
                          })}
                        </span>
                        <span>{copy.formatPercent(point.retentionRate)}</span>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            )}
          </For>
        </Show>
      </CardContent>
    </Card>
  );
}

/**
 * Props for rendering the SolidJS analytics dashboard page.
 *
 * @property data Optional aggregate dashboard data rendered across the page sections.
 * @property recentEvents Optional list of recent analytics events shown in the event table.
 * @property funnelResult Optional funnel analysis rendered in the conversion section.
 * @property cohortAnalysis Optional cohort-retention payload rendered in the cohort section.
 * @property realtimeStats Optional realtime visitor summary for the page header.
 * @property timelineEvents Optional event timeline rows.
 * @property consentSettings Optional persisted consent state summary.
 * @property isLoading Whether child widgets should render loading placeholders instead of content.
 * @property error Optional page-level error surfaced before dashboard data sections.
 * @property onRetry Optional recovery callback shown in the page-level error state.
 * @property class Optional class name merged into the page wrapper.
 */
export interface AnalyticsDashboardPageProps {
  data?: AnalyticsDashboardData | null;
  recentEvents?: AnalyticsEvent[];
  funnelResult?: FunnelResult | null;
  cohortAnalysis?: CohortAnalysis | null;
  realtimeStats?: RealtimeStats | null;
  timelineEvents?: TimelineEvent[];
  consentSettings?: ConsentSettings | null;
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

/**
 * Renders the SolidJS analytics dashboard page with summary cards and detail tables.
 *
 * @param props Page props containing dashboard aggregates, recent events, and loading state.
 * @returns A full-page SolidJS analytics dashboard composition.
 */
export const AnalyticsDashboardPage: Component<AnalyticsDashboardPageProps> = (
  props,
) => {
  const copy = createAnalyticsCopy();
  const seoMeta = buildAnalyticsSeoMeta({
    title: copy.t("analytics.seo.dashboardTitle"),
    description: copy.t("analytics.seo.dashboardDescription"),
    path: "/analytics",
    keywords: ["analytics", "traffic", "engagement", "funnels", "events"],
  });

  return (
    <>
      <AnalyticsSeoHead meta={seoMeta} />
      <div
        class={cn(
          "flex min-h-screen flex-col gap-6 bg-background p-6 text-foreground",
          props.class,
        )}
        data-analytics-page="dashboard"
      >
        <PageErrorBoundary error={props.error} onRetry={props.onRetry}>
          <FadeIn>
            <header
              class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
              data-analytics-page-header
            >
              <div class="flex flex-col gap-1">
                <h1 class="text-3xl font-bold tracking-tight">
                  {copy.t("analytics.dashboard.title")}
                </h1>
                <p class="text-sm text-muted-foreground">
                  {copy.t("analytics.dashboard.subtitle")}
                </p>
              </div>
              <RealtimeBadge
                stats={props.realtimeStats ?? null}
                isLoading={props.isLoading ?? false}
              />
            </header>
          </FadeIn>

          <FadeIn
            delay={60}
            class="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
            data-analytics-stats-row
          >
            <StatsCard
              label={copy.t("analytics.dashboard.pageViews")}
              value={
                props.data ? copy.formatNumber(props.data.totalPageViews) : "-"
              }
              icon="Views"
            />
            <StatsCard
              label={copy.t("analytics.dashboard.uniqueVisitors")}
              value={
                props.data ? copy.formatNumber(props.data.uniqueVisitors) : "-"
              }
              icon="Users"
            />
            <StatsCard
              label={copy.t("analytics.dashboard.topReferrers")}
              value={copy.formatNumber(props.data?.topReferrers?.length ?? 0)}
              icon="Links"
            />
            <StatsCard
              label={copy.t("analytics.dashboard.devices")}
              value={copy.formatNumber(
                Object.keys(props.data?.browserBreakdown ?? {}).length,
              )}
              icon="Devices"
            />
          </FadeIn>

          <FadeIn delay={120}>
            <h2 class="sr-only">{copy.t("analytics.dashboard.overview")}</h2>
            <Card data-analytics-overview-section>
              <CardHeader class="pb-0">
                <CardTitle class="text-lg" data-analytics-section-title>
                  {copy.t("analytics.dashboard.overview")}
                </CardTitle>
              </CardHeader>
              <CardContent
                class="grid gap-4 pt-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]"
                data-analytics-grid
              >
                <TopPagesTable
                  pages={props.data?.topPages ?? []}
                  isLoading={props.isLoading}
                />
                <DeviceBreakdown
                  breakdown={
                    props.data?.deviceBreakdown ?? {
                      desktop: 0,
                      tablet: 0,
                      mobile: 0,
                    }
                  }
                />
              </CardContent>
            </Card>
          </FadeIn>

          <FadeIn delay={150}>
            <div class="grid gap-4 xl:grid-cols-2">
              <FunnelChart
                result={props.funnelResult ?? emptyFunnelResult(copy)}
              />
              {renderCohorts(props.cohortAnalysis, copy)}
            </div>
          </FadeIn>

          <FadeIn delay={165}>
            {renderConsentStatus(props.consentSettings, copy)}
          </FadeIn>

          <FadeIn delay={180}>
            <h2 class="sr-only">
              {copy.t("analytics.dashboard.recentEvents")}
            </h2>
            <Card data-analytics-section>
              <CardHeader class="pb-0">
                <CardTitle class="text-lg" data-analytics-section-title>
                  {copy.t("analytics.dashboard.recentEvents")}
                </CardTitle>
              </CardHeader>
              <CardContent class="grid gap-4 pt-4 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
                <Timeline events={props.timelineEvents ?? []} maxVisible={10} />
                <EventTable
                  events={props.recentEvents ?? []}
                  isLoading={props.isLoading}
                  maxRows={15}
                />
              </CardContent>
            </Card>
          </FadeIn>
        </PageErrorBoundary>
      </div>
    </>
  );
};
